import {IServiceFactory} from "../../service-factory.interface";
import {BookingFlowStrategy} from "./booking-flow/booking-flow.strategy";
import {IDotRezBookingSession} from "../../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";
import {IDotRezBookingSessionData} from "../../dot-rez-api/data-contracts/booking/dot-rez-booking-session-data.interface";
import {
    IFlightSearchControllerViewModel,
    IFlightSearchFields
} from "../../flight-search/flight-search-controller/flight-search-controller-view-model.interface";
import {FlightSearchControllerModel} from "../../flight-search/flight-search-controller/flight-search-controller.model";
import {createEmptyBookingSessionData} from "../models/create-empty-booking-session-data";
import {IDotRezInitialBookingData} from "./booking-initial-data.interface";
import {IBookingStateQueryBuilder} from "../../dot-rez-api/graph-ql/queries/booking-state-query-builder.interface";
import {IBookingStrategyOptions} from "./booking-strategy-base";
import {IDotRezBookingData} from "../../dot-rez-api/data-contracts/booking/booking-state/booking-state.data-contracts";
import {ManageMyBookingStrategy} from "./manage-my-booking/manage-my-booking.strategy";
import {CheckInStrategy} from "./check-in/check-in.strategy";
import {ViewBookingDetailsStrategy} from "./view-booking-details/view-booking-details.strategy";
import {TransientBookingStrategy} from "./view-booking-details/transient-booking.strategy";
import {IPassengerTypeSelectorsList} from "../../passenger-types/passengers-types.service.interface";
import {PassengerTypeCodesEnum} from "../../passenger-types/passenger-type-codes.enum";
import {UserSessionStorageKeys} from "../../storage/user-session-storage/user-session-storage-keys";
import {Station} from "../../stations/station.service.interface";
import {NullableDate} from "../../../types/nullable-types";


interface IRestoreBookingOptions {
    createQueryBuilder: (bookingSession: IDotRezBookingSession) => IBookingStateQueryBuilder;
}

export class BookingStrategyFactory {
    constructor(private readonly services: IServiceFactory) {

    }

    async startNewBookingStrategy(bookingSession: IDotRezBookingSession, searchController: IFlightSearchControllerViewModel): Promise<BookingFlowStrategy> {
        return new BookingFlowStrategy(this.services, {
            initialBookingData: null,
            bookingSession: bookingSession,
            bookingSessionData: createEmptyBookingSessionData(this.services.currency.current),
            searchController: searchController
        });
    }

    async tryRestoreBookingStrategy(): Promise<BookingFlowStrategy | null> {
        const strategyOptions = await this._tryRestoreStrategy({
            createQueryBuilder: bookingSession => this._createFullBookingStateQueryBuilder(bookingSession)
        });

        if(strategyOptions) {
            return new BookingFlowStrategy(this.services, strategyOptions);
        }

        return null;
    }



    async createManageMyBookingStrategy(bookingSession: IDotRezBookingSession, bookingData: IDotRezBookingData): Promise<ManageMyBookingStrategy> {
        await this._impersonateForPso(bookingSession, bookingData);
        const queryBuilder = this._createFullBookingStateQueryBuilder(bookingSession, bookingData).useSegmentsDisruptionDelays();
        const bookingSessionData = this._createFullBookingSessionData(await queryBuilder.getBookingState());
        const searchController = await this._createSearchController(bookingSession, bookingSessionData.bookingData);

        return new ManageMyBookingStrategy(this.services, {
            bookingSession: bookingSession,
            bookingSessionData: bookingSessionData,
            searchController: searchController,
            initialBookingData: this._cloneObject({
                bookingData: bookingData,
                segmentsDelays: bookingSessionData.segmentsDelays
            })

        });
    }

    async tryRestoreManageMyBookingStrategy(): Promise<ManageMyBookingStrategy | null> {
        const strategyOptions = await this._tryRestoreStrategy({
            createQueryBuilder: bookingSession => this._createFullBookingStateQueryBuilder(bookingSession).useSegmentsDisruptionDelays()
        });

        if(strategyOptions) {
            return new ManageMyBookingStrategy(this.services, strategyOptions);
        }

        return null;
    }

    async createCheckInStrategy(bookingSession: IDotRezBookingSession, bookingData: IDotRezBookingData): Promise<CheckInStrategy> {
        const queryBuilder = this._createFullBookingStateQueryBuilder(bookingSession, bookingData);
        const bookingSessionData = this._createFullBookingSessionData(await queryBuilder.getBookingState());
        const searchController = await this._createSearchController(bookingSession, bookingSessionData.bookingData);

        return new CheckInStrategy(this.services, {
            bookingSession: bookingSession,
            bookingSessionData: bookingSessionData,
            searchController: searchController,
            initialBookingData: this._cloneObject({
                bookingData: bookingData,
                segmentsDelays: []
            })
        });
    }


    async tryRestoreCheckInStrategy(): Promise<CheckInStrategy | null> {
        const strategyOptions = await this._tryRestoreStrategy({
            createQueryBuilder: bookingSession => this._createFullBookingStateQueryBuilder(bookingSession)
        });

        if(strategyOptions) {
            return new CheckInStrategy(this.services, strategyOptions);
        }

        return null;
    }

    async createViewBookingDetailsStrategy(bookingSession: IDotRezBookingSession, bookingData: IDotRezBookingData): Promise<ViewBookingDetailsStrategy> {
        const queryBuilder = bookingSession.bookingStateQueryBuilder(bookingData).useBundlesAvailability().useCheckInRequirements();
        const bookingSessionData = this._createFullBookingSessionData(await queryBuilder.getBookingState());
        const searchController = await this._createSearchController(bookingSession, bookingSessionData.bookingData);

        return new ViewBookingDetailsStrategy(this.services, {
            bookingSession: bookingSession,
            bookingSessionData: bookingSessionData,
            searchController: searchController,
            initialBookingData: this._cloneObject({
                bookingData: bookingData,
                segmentsDelays: []
            })
        });
    }

    async tryRestoreViewBookingDetailsStrategy(): Promise<ViewBookingDetailsStrategy | null> {
        const strategyOptions = await this._tryRestoreStrategy({
            createQueryBuilder: bookingSession => bookingSession.bookingStateQueryBuilder().useBookingData().useBundlesAvailability().useCheckInRequirements()
        });

        if(strategyOptions) {
            return new ViewBookingDetailsStrategy(this.services, strategyOptions);
        }

        return null;
    }

    async createTransientBookingStrategy(bookingSession: IDotRezBookingSession, bookingData: IDotRezBookingData): Promise<TransientBookingStrategy> {
        const queryBuilder = this._createFullBookingStateQueryBuilder(bookingSession);
        const bookingSessionData = this._createFullBookingSessionData(await queryBuilder.getBookingState());
        const searchController = await this._createSearchController(bookingSession, bookingSessionData.bookingData);

        return new TransientBookingStrategy(this.services, {
            bookingSession: bookingSession,
            bookingSessionData: bookingSessionData,
            searchController: searchController,
            initialBookingData: this._cloneObject({
                bookingData: bookingData,
                segmentsDelays: []
            })
        });
    }

    private _cloneObject<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    private _createFullBookingSessionData(partialData: Partial<IDotRezBookingSessionData>): IDotRezBookingSessionData {
        return {
            ...createEmptyBookingSessionData(partialData.bookingData?.currencyCode ?? this.services.currency.current),
            ...partialData
        };
    }

    private _getBookDate(bookingData: IDotRezBookingData): NullableDate {
        if (bookingData.info?.createdDate) {
            return this.services.time.parseIsoDate(bookingData.info.createdDate);
        }

        if(bookingData.info?.bookedDate) {
            return this.services.time.parseIsoDate(bookingData.info.bookedDate);
        }

        return null;
    }

    private async _createSearchController(bookingSession: IDotRezBookingSession, bookingData: IDotRezBookingData): Promise<FlightSearchControllerModel> {

        const initialFieldsValues =  this._createSearchControllerInitialFieldsValues(bookingData)
        return await FlightSearchControllerModel.createAsync(this.services, UserSessionStorageKeys.ActiveBookingSearchControllerStorage, {
            bookingSession: bookingSession,
            initialFieldsValues: initialFieldsValues,
            bookingDate: this._getBookDate(bookingData)
        });
    }

    private _createSearchControllerInitialFieldsValues(bookingData: IDotRezBookingData): undefined | Partial<IFlightSearchFields> {
        const journeys = bookingData.journeys ?? [];
        if(journeys.length === 0) {
            return undefined;
        }

        const psoMarket = this.services.stations.getPsoMarket(this.services.stations.tryGetStation(journeys[0].designator.origin),
                                                                            this.services.stations.tryGetStation(journeys[0].designator.destination),{
                bookingDate: this.services.time.parseIsoDate(bookingData.info.bookedDate),
                departureDate: this.services.time.parseIsoDate(bookingData.journeys[0].designator.departure)
            });

        const initialPsoUserOptions = psoMarket?.inferPsoUserOptionsFromBookingData(bookingData) ?? undefined;

        return {
            psoUserOption: initialPsoUserOptions?.psoUserOption,
            psoUserAgreement: initialPsoUserOptions?.psoUserAgreement,
            allowBogo: false, //this.services.navigator.routes.booking.hasActiveChildRoute, //only on booking flow we allow BOGO
            departureOrigin:  this.services.stations.tryGetStation(journeys[0]?.designator.origin) ?? undefined,
            departureDestination:  this.services.stations.tryGetStation(journeys[0]?.designator?.destination) ?? undefined,
            returnOrigin:  this.services.stations.tryGetStation(journeys[1]?.designator?.origin) ?? undefined,
            returnDestination:  this.services.stations.tryGetStation(journeys[1]?.designator?.destination) ?? undefined,
            passengers: this._getPassengersTypesSelectors(bookingData),
            //withBlueBenefits: this.booking.blueBenefits.isBookingWithBlueBenefits
        }
    }

    private _getPassengersTypesSelectors(bookingData: IDotRezBookingData): IPassengerTypeSelectorsList | undefined{

        if(bookingData.passengers.length === 0) {
            return undefined;
        }

        const typesCodesCount: Record<string, number> = {};

        let infantsCount = 0;
        for(let p of bookingData.passengers) {
            if(!typesCodesCount[p.value.passengerTypeCode]) {
                typesCodesCount[p.value.passengerTypeCode] = 0;
            }
            typesCodesCount[p.value.passengerTypeCode]++;
            if(p.value.infant) {
                infantsCount++;
            }
        }

        if(infantsCount > 0) {
            typesCodesCount[PassengerTypeCodesEnum.Infant] = infantsCount;
        }



        return this.services.passengerTypes.createReadonlyPassengersTypesSelectorsList(typesCodesCount);


    }

    private _createFullBookingStateQueryBuilder(bookingSession: IDotRezBookingSession, initialBookingData?: IDotRezBookingData): IBookingStateQueryBuilder {
        let builder: IBookingStateQueryBuilder;
        if(initialBookingData) {
            builder = bookingSession.bookingStateQueryBuilder(initialBookingData);
        } else {
            builder = bookingSession.bookingStateQueryBuilder().useBookingData()
        }
        return builder.useBundlesAvailability()
                      .useSsrsAvailability()
                      .useSeatMapsAvailability()
                      .useCheckInRequirements();
    }

    private async _tryRestoreStrategy(options: IRestoreBookingOptions): Promise<IBookingStrategyOptions | null> {
        await this.services.user.profile.waitForProfileInitialization();

        const bookingSession = await this._tryRestoreBookingSession();
        if(!bookingSession) {
            return null;
        }

        const queryBuilder = options.createQueryBuilder(bookingSession);
        const bookingSessionData = this._createFullBookingSessionData(await queryBuilder.getBookingState());
        const searchController = await this._createSearchController(bookingSession, bookingSessionData.bookingData);

        await searchController.applySearch();

        let initialBookingData = this.services.userSessionStorage.getJson<IDotRezInitialBookingData>(UserSessionStorageKeys.ActiveBookingInitialData) ?? null;

        return {
            bookingSession: bookingSession,
            bookingSessionData: this._createFullBookingSessionData(bookingSessionData),
            searchController: searchController,
            initialBookingData: initialBookingData
        };
    }


    private async _tryRestoreBookingSession(): Promise<IDotRezBookingSession | null> {

        const token = this.services.userSessionStorage.getItem(UserSessionStorageKeys.ActiveBookingToken);
        if(!token) {
            return null;
        }

        try {
            return await this.services.dotRezApi.loadBookingSession(token);
        } catch (err) {
            this.services.logger.warning('Failed to load availability from storage', err);
            return null;
        }
    }

    private _getStation(stationCode: string): Station {
        return this.services.stations.getStation(stationCode);
    }

    private async _impersonateForPso(bookingSession: IDotRezBookingSession, bookingData: IDotRezBookingData): Promise<void> {

        if(this.services.user.isAuthorized) {
            return;
        }

        const bundleCode = bookingData.journeys[0]?.segments[0]?.passengerSegment[0]?.value?.bundleCode;

        if(bundleCode && this.services.bundleTypes.getBundleType(bundleCode).requireBookingImpersonationForPso) {
            const psoMarket = this.services.stations.getPsoMarket(this._getStation(bookingData.journeys[0].designator.origin),
                                                                                this._getStation(bookingData.journeys[0].designator.destination), {
                        bookingDate: this.services.time.parseIsoDate(bookingData.info.bookedDate),
                        departureDate: this.services.time.parseIsoDate(bookingData.journeys[0].designator.departure)
                });
            const psoAgentName = psoMarket?.getAgentForBundle(bundleCode);
            if(psoAgentName) {
                await bookingSession.impersonateWithAgent({
                    agent: psoAgentName
                });
            }
        }
    }
}
